import type { LocalizedTextInput } from '@mybonus/public';
import { useAPI, useMutation, useQuery, APIInput, useForm } from '@mybonus/ui';
import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export function useFaqItemUpsert() {
	const params = useParams();
	const { api } = useAPI();
	const create = useMutation<APIInput['admin']['faq']['item']['create']>(
		(input) => api.admin.faq.item.create.mutate(input),
	);

	const singleInput = useMemo(() => ({ id: Number(params.id) }), [params.id]);

	const single = useQuery(
		'admin.faq.item.single',
		(input) => api.admin.faq.item.single.query(input),
		singleInput,
		{ preventAutoload: !params.id, ttl: false },
	);

	const mode = single.data ? 'Update' : 'Create';

	const update = useMutation<APIInput['admin']['faq']['item']['update']>(
		(input) => api.admin.faq.item.update.mutate(input),
	);

	const groups = useQuery(
		'admin.faq.group.list',
		(args) => api.admin.faq.group.list.query(args),
		undefined,
		{ ttl: false },
	);

	const form = useForm<{
		question: LocalizedTextInput;
		answer: LocalizedTextInput;
		groupId: number | null;
		prio: number;
		active: boolean;
	}>({
		onSubmit: () => {
			if (single.data?.id) {
				update.mutate({ id: single.data.id, data: form.data });
			} else {
				create.mutate(form.data);
				form.reset();
			}
		},
		validations: {
			question: {
				required: {
					message: 'Question is required',
				},
			},
			answer: {
				required: {
					message: 'Answer is required',
				},
			},
			prio: {
				required: {
					message: 'Item prio is required',
				},
			},
		},
		initialValues: {
			prio: 3,
			active: true,
		},
	});

	function handleReset() {
		create.reset();
		update.reset();
	}

	const isSuccess = !!create.isSuccess || !!update.isSuccess;
	const isLoading = single.isLoading || create.isLoading || update.isLoading;
	const error = single.error || create.error || update.error;
	const notFound = params.id && !isLoading && !single.data;

	useEffect(() => {
		if (single.data) {
			form.setData({
				...form.data,
				question: single.data.question,
				answer: single.data.answer,
				groupId: single.data.groupId,
				prio: single.data.prio,
				active: !!single.data.active,
			});
		}
	}, [single.data]);

	return {
		groups,
		isSuccess,
		isLoading,
		error,
		notFound,
		params,
		single,
		mode,
		form,
		handleReset,
	};
}
